import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, GraduationCap, Info, Check, Save, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Re-usable "Mes formations enseignables" form.
 *
 * Used by:
 *  - Onboarding (mode="onboarding") → calls onSubmitted() after successful PUT
 *  - Standalone edit page (mode="edit")  → shows toast + own save button
 */
export default function FormationsTaughtForm({ mode = "edit", onSubmitted }) {
    const [cursus, setCursus] = useState([]);
    const [formations, setFormations] = useState([]);
    const [selected, setSelected] = useState([]);
    const [openCursus, setOpenCursus] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/cursus"),
            api.get("/formations"),
            api.get("/formateur/formations-taught"),
        ])
            .then(([rc, rf, rt]) => {
                if (!alive) return;
                setCursus(rc.data || []);
                setFormations(rf.data || []);
                setSelected((rt.data?.slugs || []).slice());
                // Open all cursus accordions by default
                const openAll = {};
                (rc.data || []).forEach((c) => { openAll[c.slug] = true; });
                setOpenCursus(openAll);
            })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const formationsByCursus = useMemo(() => {
        const m = {};
        formations.forEach((f) => {
            (m[f.cursus_slug] ||= []).push(f);
        });
        // Sort each list by `order` then name for stable display
        Object.values(m).forEach((arr) => arr.sort((a, b) => (a.order ?? 99) - (b.order ?? 99) || a.name.localeCompare(b.name)));
        return m;
    }, [formations]);

    const totalFormations = formations.length;
    const isSelected = (slug) => selected.includes(slug);

    const toggle = (slug) => {
        setSelected((curr) => curr.includes(slug) ? curr.filter((s) => s !== slug) : [...curr, slug]);
    };

    const allSelectedForCursus = (cursusSlug) => {
        const list = formationsByCursus[cursusSlug] || [];
        return list.length > 0 && list.every((f) => selected.includes(f.slug));
    };

    const toggleCursusAll = (cursusSlug) => {
        const list = (formationsByCursus[cursusSlug] || []).map((f) => f.slug);
        if (allSelectedForCursus(cursusSlug)) {
            setSelected((curr) => curr.filter((s) => !list.includes(s)));
        } else {
            setSelected((curr) => Array.from(new Set([...curr, ...list])));
        }
    };

    const save = async () => {
        if (selected.length === 0) {
            toast.error("Sélectionnez au moins une formation pour valider cette étape.");
            return;
        }
        setSaving(true);
        try {
            await api.put("/formateur/formations-taught", { slugs: selected });
            toast.success(mode === "onboarding" ? "Formations enregistrées" : "Vos formations enseignables ont été mises à jour");
            onSubmitted && (await onSubmitted());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="py-12 text-center text-sm text-brand-smoke" data-testid="formations-taught-loading">Chargement des formations…</div>;
    }

    return (
        <div className="space-y-6" data-testid="formations-taught-form">
            <div className="flex items-start gap-3 rounded-2xl border border-brand-line bg-white p-5">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                    <ListChecks className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h2 className="font-display text-2xl font-medium tracking-tightest">Mes formations enseignables</h2>
                    <p className="mt-1 text-sm text-brand-smoke">
                        Sélectionnez les formations que vous êtes en mesure d'animer. Cette information alimente le matching automatique des nouvelles sessions. Vous pourrez modifier cette liste plus tard depuis votre espace.
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                {cursus.map((c) => {
                    const list = formationsByCursus[c.slug] || [];
                    const open = !!openCursus[c.slug];
                    const allOn = allSelectedForCursus(c.slug);
                    const selectedCount = list.filter((f) => isSelected(f.slug)).length;
                    return (
                        <div key={c.slug} className="overflow-hidden rounded-2xl border border-brand-line bg-white" data-testid={`taught-cursus-${c.slug}`}>
                            <button
                                type="button"
                                onClick={() => setOpenCursus((s) => ({ ...s, [c.slug]: !open }))}
                                className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-[#FAFAFA]"
                                data-testid={`taught-cursus-toggle-${c.slug}`}
                            >
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                    <GraduationCap className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-display text-lg font-medium tracking-tightest">Cursus {c.name}</span>
                                        <span className="rounded-full bg-brand-orange/10 px-2 py-0.5 text-[10px] font-semibold text-brand-orange">{selectedCount}/{list.length}</span>
                                    </div>
                                    {c.tagline && <div className="text-xs text-brand-smoke">{c.tagline}</div>}
                                </div>
                                <ChevronDown className={`h-4 w-4 text-brand-smoke transition-transform ${open ? "rotate-180" : ""}`} />
                            </button>
                            <AnimatePresence initial={false}>
                                {open && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                                    >
                                        <div className="border-t border-brand-line px-5 py-4">
                                            <div className="mb-3 flex items-center justify-between gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleCursusAll(c.slug)}
                                                    data-testid={`taught-cursus-all-${c.slug}`}
                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-brand-orange hover:underline"
                                                >
                                                    {allOn ? "Tout désélectionner" : "Tout sélectionner ce cursus"}
                                                </button>
                                            </div>
                                            <div className="grid gap-2">
                                                {list.map((f) => {
                                                    const on = isSelected(f.slug);
                                                    return (
                                                        <label
                                                            key={f.slug}
                                                            className={`group flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
                                                                on
                                                                    ? "border-brand-orange bg-brand-orange/5"
                                                                    : "border-brand-line bg-white hover:border-brand-orange/40"
                                                            }`}
                                                            data-testid={`taught-formation-${f.slug}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={on}
                                                                onChange={() => toggle(f.slug)}
                                                                data-testid={`taught-checkbox-${f.slug}`}
                                                                className="mt-1 h-4 w-4 accent-brand-orange"
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-semibold text-brand-ink">{f.name}</span>
                                                                    {f.short_description && (
                                                                        <span className="relative inline-flex" title={f.short_description}>
                                                                            <Info className="h-3.5 w-3.5 text-brand-smoke hover:text-brand-orange" />
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {f.short_description && (
                                                                    <p className="mt-1 text-xs text-brand-smoke line-clamp-2">{f.short_description}</p>
                                                                )}
                                                            </div>
                                                            {on && <Check className="h-4 w-4 flex-shrink-0 text-brand-orange" />}
                                                        </label>
                                                    );
                                                })}
                                                {list.length === 0 && (
                                                    <div className="text-xs text-brand-smoke">Aucune formation publiée dans ce cursus pour le moment.</div>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>

            <div className="sticky bottom-0 z-10 -mx-2 mt-6 flex items-center justify-between gap-4 rounded-2xl border border-brand-line bg-white/95 px-5 py-4 shadow-premium backdrop-blur" data-testid="taught-footer">
                <div className="text-sm">
                    <span className="font-display text-2xl font-medium tracking-tightest text-brand-orange">{selected.length}</span>
                    <span className="ml-2 text-brand-smoke">
                        formation{selected.length > 1 ? "s" : ""} sélectionnée{selected.length > 1 ? "s" : ""} sur {totalFormations}
                    </span>
                    {selected.length === 0 && (
                        <span className="ml-3 text-xs text-red-600">Sélectionnez au moins 1 formation pour valider.</span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || selected.length === 0}
                    data-testid="taught-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-50"
                >
                    <Save className="h-3.5 w-3.5" />
                    {saving ? "Enregistrement…" : (mode === "onboarding" ? "Valider et continuer" : "Enregistrer")}
                </button>
            </div>
        </div>
    );
}
